import React, { useState, useEffect } from 'react';
import Button from '../../components/common/button/Button';
import Modal from '../../components/common/model/Modal';
import styles from './CompanyPage.module.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import companyService from '../../services/companyService';
import InputField from '../../components/common/inputField/InputField';
import { Company } from '../../types/companyTypes';

const CompanyPage: React.FC<{ role?: string }> = ({ role = 'admin' }) => {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    website: '',
    gst: '',
    is_active: true
  });

  const fetchCompany = async () => {
    try {
      const response = await companyService.getCompany('1'); // Replace with actual company ID
      
      if (response && typeof response === 'object') {
        const normalizedCompany: Company = {
          id: response.id ?? '',
          name: response.name ?? '',
          email: response.email ?? '',
          phone: response.phone ?? '',
          address: response.address ?? '',
          city: response.city ?? '',
          state: response.state ?? '',
          zip_code: response.zip_code ?? '',
          country: response.country ?? '',
          website: response.website ?? '',
          gst: response.gst ?? '',
          is_active: response.is_active ?? true,
          created_at: response.created_at ?? new Date().toISOString(),
          updated_at: response.updated_at ?? new Date().toISOString(),
        };

        setCompany(normalizedCompany);
        setForm({
          name: normalizedCompany.name,
          email: normalizedCompany.email,
          phone: normalizedCompany.phone,
          address: normalizedCompany.address,
          city: normalizedCompany.city,
          state: normalizedCompany.state,
          zip_code: normalizedCompany.zip_code,
          country: normalizedCompany.country,
          website: normalizedCompany.website,
          gst: normalizedCompany.gst,
          is_active: true
        });
      }
    } catch (err) {
      console.error('Error fetching company:', err);
      toast.error('Failed to fetch company details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompany();
  }, []);

  const handleUpdate = async () => {
    if (!form.name.trim()) {
      toast.error('Company name is required.');
      return;
    }

    if (!company) return;

    const data = {
      id: company.id,
      name: form.name,
      email: form.email,
      phone: form.phone,
      address: form.address,
      city: form.city,
      state: form.state,
      zip_code: form.zip_code,
      country: form.country,
      website: form.website,
      gst: form.gst,
      is_active: form.is_active
    };

    try {
      await companyService.updateCompany(data);
      toast.success('Company details updated successfully!');
      setShowModal(false);
      fetchCompany();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update company';
      toast.error(message);
    }
  };

  const handleOpenModal = () => {
    if (company) {
      setForm({
        name: company.name,
        email: company.email,
        phone: company.phone,
        address: company.address,
        city: company.city,
        state: company.state,
        zip_code: company.zip_code,
        country: company.country,
        website: company.website,
        gst: company.gst,
        is_active: true
      });
    }
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loader}></div>
          <p>Loading company details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
  
      <div className={styles.header}>

        <h4>Company Profile</h4>
        
        <div className={styles.headerRight}>
          {role === 'admin' && (
            <Button variant="primary" onClick={handleOpenModal}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                <path d="M12 20h9"/>
                <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z"/>
              </svg>
              Edit Company
            </Button>
          )}
        </div>
      </div>

      {company ? (
        <div className={styles.profileCard}>
          <div className={styles.cardHeader}>
            <div className={styles.companyIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18"/>
                <path d="M9 8h1"/>
                <path d="M9 12h1"/>
                <path d="M9 16h1"/>
                <path d="M14 8h1"/>
                <path d="M14 12h1"/>
                <path d="M14 16h1"/>
                <path d="M6 3v18"/>
                <path d="M18 3v18"/>
                <path d="M6 3h12"/>
              </svg>
            </div>
            <div className={styles.companyHeader}>
              <h2 className={styles.companyName}>{company.name}</h2>
              <span className={`${styles.statusBadge} ${company.is_active ? styles.active : styles.inactive}`}>
                {company.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="m2 7 10 6 10-6"/>
                </svg>
                Email
              </div>
              <div className={styles.infoValue}>{company.email || '-'}</div>
            </div>

            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                Phone
              </div>
              <div className={styles.infoValue}>{company.phone || '-'}</div>
            </div>

            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                Website
              </div>
              <div className={styles.infoValue}>
                {company.website ? (
                  <a href={`https://${company.website}`} target="_blank" rel="noopener noreferrer" className={styles.link}>
                    {company.website}
                  </a>
                ) : '-'}
              </div>
            </div>

            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12" y2="12"/>
                  <line x1="9" y1="15" x2="15" y2="15"/>
                </svg>
                GST Number
              </div>
              <div className={styles.infoValue}>{company.gst || '-'}</div>
            </div>

            <div className={`${styles.infoItem} ${styles.fullWidth}`}>
              <div className={styles.infoLabel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                Address
              </div>
              <div className={styles.infoValue}>{company.address || '-'}</div>
            </div>

            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                  <circle cx="12" cy="9" r="2.5"/>
                </svg>
                City
              </div>
              <div className={styles.infoValue}>{company.city || '-'}</div>
            </div>

            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                State
              </div>
              <div className={styles.infoValue}>{company.state || '-'}</div>
            </div>

            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Zip Code
              </div>
              <div className={styles.infoValue}>{company.zip_code || '-'}</div>
            </div>

            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M2 12h20"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                Country
              </div>
              <div className={styles.infoValue}>{company.country || '-'}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.noData}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
            <path d="M3 21h18"/>
            <path d="M9 8h1"/>
            <path d="M9 12h1"/>
            <path d="M9 16h1"/>
            <path d="M14 8h1"/>
            <path d="M14 12h1"/>
            <path d="M14 16h1"/>
            <path d="M6 3v18"/>
            <path d="M18 3v18"/>
            <path d="M6 3h12"/>
          </svg>
          <p>No company details found</p>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Edit Company Details"
        size='lg'
      >
        <div className={styles.modalForm}>
          <InputField
            label="Company Name"
            name="name"
            type="text"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="Enter company name"
            required
          />
          <InputField
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="Enter email address"
          />
          <InputField
            label="Phone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
            placeholder="Enter phone number"
          />
          <InputField
            label="Address"
            name="address"
            type="text"
            value={form.address}
            onChange={e => setForm({ ...form, address: e.target.value })}
            placeholder="Enter address"
          />
          <InputField
            label="City"
            name="city"
            type="text"
            value={form.city}
            onChange={e => setForm({ ...form, city: e.target.value })}
            placeholder="Enter city"
          />
          <InputField
            label="State"
            name="state"
            type="text"
            value={form.state}
            onChange={e => setForm({ ...form, state: e.target.value })}
            placeholder="Enter state"
          />
          <InputField
            label="Zip Code"
            name="zip_code"
            type="text"
            value={form.zip_code}
            onChange={e => setForm({ ...form, zip_code: e.target.value })}
            placeholder="Enter zip code"
          />
          <InputField
            label="Country"
            name="country"
            type="text"
            value={form.country}
            onChange={e => setForm({ ...form, country: e.target.value })}
            placeholder="Enter country"
          />
          <InputField
            label="Website"
            name="website"
            type="text"
            value={form.website}
            onChange={e => setForm({ ...form, website: e.target.value })}
            placeholder="Enter website URL"
          />
          <InputField
            label="GST Number"
            name="gst"
            type="text"
            value={form.gst}
            onChange={e => setForm({ ...form, gst: e.target.value })}
            placeholder="Enter GST number"
          />

          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdate}
              className={styles.submitButton}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default CompanyPage;